<template>
  <div class="min-h-screen font-sans bg-sky-50">
    <Head>
      <Title>{{ $t('meta.manutentionTitle') }}</Title>
      <Meta name="description" :content="$t('meta.manutentionDescription')" />
    </Head>

    <!-- SHOWCASE SECTION -->
    <section class="bg-sky-50 pt-6 sm:pt-8 pb-0 overflow-hidden">

      <!-- Section Header - Reduced vertical spacing -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8" v-animate-on-scroll>
        <div class="flex items-center gap-3 text-blue-600 font-bold uppercase tracking-[0.25em] text-[10px] mb-3">
          <span class="w-8 h-[1px] bg-blue-400 flex-shrink-0"></span>
          <span>{{ $t('manutention.projects.badge') }}</span>
        </div>
        
        <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 class="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight leading-tight max-w-2xl">
              {{ $t('manutention.projects.title') }}
            </h1>
          </div>
          <p class="text-slate-500 text-sm sm:text-base font-light max-w-sm leading-relaxed md:text-right">
            {{ $t('manutention.projects.subtitle') }}
          </p>
        </div>
        
        <!-- Subtle divider -->
        <div class="mt-8 h-[1px] bg-gradient-to-r from-blue-200 via-blue-100 to-transparent opacity-60"></div>
      </div>

      <!-- Carousel Container -->
      <div class="relative">
        <div v-if="pending" class="py-20 flex justify-center">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
        <ShowcaseCarousel v-else-if="projects && projects.length > 0" :items="projects" />
        <div v-else class="py-20 text-center text-slate-400 px-4">
          <i class="fas fa-inbox text-5xl mb-4 opacity-20 block"></i>
          <h3 class="text-xl font-bold mb-2 text-slate-600">{{ $t('manutention.projects.empty.title') }}</h3>
          <p class="text-sm">{{ $t('manutention.projects.empty.subtitle') }}</p>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS SECTION -->
    <section class="py-20 lg:py-24 bg-white/40 backdrop-blur-sm relative overflow-hidden">
      <!-- Decor -->
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute -top-24 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-100/40 rounded-full blur-[120px]"></div>
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <!-- Header -->
        <div class="text-center mb-16 max-w-2xl mx-auto" v-animate-on-scroll>
          <span class="inline-block text-blue-600 font-bold tracking-[0.2em] uppercase text-[10px] mb-3 bg-blue-50 border border-blue-100 px-3 py-1 rounded-full">
            {{ $t('manutention.workflow.badge') }}
          </span>
          <h2 class="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mb-4 tracking-tight leading-tight">
            {{ $t('manutention.workflow.title') }}
          </h2>
          <p class="text-slate-500 text-base sm:text-lg font-light leading-relaxed">
            {{ $t('manutention.workflow.subtitle') }}
          </p>
        </div>

        <!-- Steps Grid -->
        <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="(step, i) in 3"
            :key="i"
            class="relative p-6 sm:p-8 bg-white/80 backdrop-blur-md border border-white rounded-2xl shadow-sm hover:shadow-xl hover:shadow-blue-900/5 hover:-translate-y-1 transition-all duration-500 group overflow-hidden"
            v-animate-on-scroll
            :style="{ transitionDelay: `${i * 150}ms` }"
          >
            <!-- Step number bg -->
            <span class="absolute -bottom-4 -right-2 text-8xl sm:text-9xl font-black text-blue-600/[0.03] select-none pointer-events-none leading-none italic">
              0{{ i + 1 }}
            </span>

            <div class="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl flex items-center justify-center mb-5 sm:mb-6 shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
              <i :class="['fas text-lg sm:text-xl', ['fa-tools', 'fa-truck-loading', 'fa-globe'][i]]"></i>
            </div>
            <h3 class="text-xl sm:text-2xl font-black text-slate-900 mb-3 tracking-tight">
              {{ $t(`manutention.workflow.step${i + 1}.title`) }}
            </h3>
            <p class="text-slate-600 text-sm sm:text-base font-light leading-relaxed relative z-10">
              {{ $t(`manutention.workflow.step${i + 1}.description`) }}
            </p>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup lang="ts">
const { t } = useI18n()

const { data: response, pending } = await useAsyncData(
  'manutention-projects',
  () => $fetch<{ data: any[] }>('/api/v1/services', { params: { type: 'maintenance' } }),
  { server: false }
)

const projects = computed(() => response.value?.data ?? [])


useHead({
  title: t('meta.manutentionTitle'),
  meta: [
    { name: 'description', content: t('meta.manutentionDescription') },
    { property: 'og:title', content: t('meta.manutentionTitle') },
    { property: 'og:description', content: t('meta.manutentionDescription') }
  ]
});
</script>
